import type { ContributorFeedbackGroup } from "@skills-marketplace/contracts/contract-8-feedback";
import { reasonLabel } from "../lib/feedback";
import styles from "./FeedbackReasons.module.css";

interface Props {
  /** Feedback grouped per entry (#621). Reasons are NEVER merged across entries. */
  groups: ContributorFeedbackGroup[];
}

function formatVotedAt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

/**
 * The contributor-facing feedback list for one plugin's family (#621).
 *
 * Read-only by design: no reply, no threading, no moderation (AC #2). Anonymous by
 * construction — ContributorFeedbackItem carries no voter field, so there is nothing
 * here to attribute to a person.
 *
 * Reasons stay grouped by the entry they were left on, so a comment on one skill is
 * never misread as a comment on the plugin. Both directions are shown: knowing why
 * something wasn't useful is the actionable half.
 */
export default function FeedbackReasons({ groups }: Props) {
  return (
    <div className={styles.wrap}>
      {groups.map((group) => (
        <section key={group.installName} className={styles.group}>
          <h4 className={styles.groupTitle}>{group.installName}</h4>
          <ul className={styles.items}>
            {group.items.map((item, i) => (
              <li key={i} className={styles.item}>
                <span
                  className={item.direction === "up" ? styles.up : styles.down}
                  aria-label={item.direction === "up" ? "Found useful" : "Not useful"}
                >
                  {item.direction === "up" ? "Useful" : "Not useful"}
                </span>
                {item.reasonCodes.length > 0 && (
                  <span className={styles.chips}>
                    {item.reasonCodes.map((code) => (
                      <span key={code} className={styles.chip}>
                        {reasonLabel(code)}
                      </span>
                    ))}
                  </span>
                )}
                {/* Untrusted user input (contract-8): render as text, never as HTML. */}
                {item.reasonText && <p className={styles.text}>{item.reasonText}</p>}
                <span className={styles.date}>{formatVotedAt(item.votedAt)}</span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
